<template>
    <!--
        Open Dental API — per-site switch, tier and customer key (Documents/OD-API-PLANNING.md Phase 0.7).

        Everything here talks to main-service od-api-config.js, which enforces every rule server-side:
          GET  /get-od-api-config   → { OD_API, OpenDentalAPITier, keySet }   (never the key)
          POST /save-od-api-config  → the only writer of the three teamio.sites columns

        ⛔ The current value comes from that GET, NEVER from the cached `sites` row in the store or
           localStorage (plan §2 invariant 4) — the browser cache is display-only and may be stale.
        ⛔ The customer key is WRITE-ONLY: a password input excluded from session recordings
           (data-private), held only in this component while the request runs, cleared right after,
           never put in Vuex or localStorage. The page shows "Key stored: yes/no", nothing more.
    -->
    <div class="feature-card od-api-card" data-test="od-api-settings">
        <div class="feature-card-header">
            <div class="feature-icon" style="background: #e6f4ee;"><i class="fa fa-plug" style="color: #0f7259;"></i></div>
            <div class="feature-info">
                <div class="feature-name">Open Dental API</div>
                <div class="feature-desc">Write to the office's Open Dental through its official API (per-office key, paid by Teamio)</div>
            </div>
            <b-form-checkbox
                :key="toggleKey"
                :checked="config ? config.OD_API === 1 : false"
                switch
                :disabled="!config || saving || (config.OD_API !== 1 && !!blockedReason)"
                v-b-tooltip.hover.left
                :title="config && config.OD_API !== 1 ? (blockedReason || '') : ''"
                data-test="od-api-toggle"
                @change="onToggle"
            ></b-form-checkbox>
        </div>

        <div class="feature-sub-option od-api-body">
            <div v-if="loading" class="small text-muted py-1">
                <b-spinner small class="mr-1"></b-spinner>Loading Open Dental API settings&hellip;
            </div>
            <b-alert v-else-if="loadError" show variant="danger" class="mb-0 py-2 small" data-test="od-api-load-error">
                {{ loadError }}
                <b-link class="ml-1" @click="load()">Retry</b-link>
            </b-alert>
            <template v-else-if="config">
                <p class="od-api-note mb-2" data-test="od-api-requirements">{{ requirementsNote }}</p>

                <!-- Tier -->
                <div class="od-api-row">
                    <label class="od-api-label" for="od-api-tier">Tier</label>
                    <select id="od-api-tier" v-model.number="tierDraft" class="form-control form-control-sm od-api-select" :disabled="saving" data-test="od-api-tier">
                        <option :value="null" disabled>Choose a tier…</option>
                        <option v-for="t in tiers" :key="t.tier" :value="t.tier">{{ t.label }}</option>
                    </select>
                    <b-button size="sm" variant="outline-primary" :disabled="saving || !tierDraft || tierDraft === config.OpenDentalAPITier" data-test="od-api-save-tier" @click="saveTier">Save tier</b-button>
                </div>
                <ul class="od-api-legend mb-2" data-test="od-api-legend">
                    <li v-for="t in tiers" :key="t.tier" :class="{ 'od-api-current': t.tier === config.OpenDentalAPITier }">
                        <strong>{{ t.label }}</strong> {{ t.summary }}
                        <ul class="od-api-items">
                            <li v-for="it in t.items" :key="it.what">
                                {{ it.what }}<span v-if="it.requires" class="od-api-requires"> — requires {{ it.requires }}</span>
                            </li>
                        </ul>
                        <div v-if="t.later" class="od-api-later">{{ t.later }}</div>
                    </li>
                </ul>

                <!-- Customer key: write-only -->
                <div class="od-api-row">
                    <label class="od-api-label" for="od-api-key">Customer key</label>
                    <input
                        id="od-api-key"
                        v-model="keyDraft"
                        type="password"
                        data-private
                        autocomplete="new-password"
                        spellcheck="false"
                        class="form-control form-control-sm od-api-key"
                        :placeholder="config.keySet ? 'Paste a new key to replace the stored one' : 'Paste the office\'s customer key'"
                        :disabled="saving"
                        data-test="od-api-key"
                    />
                    <b-button size="sm" variant="outline-primary" :disabled="saving || !keyDraft" data-test="od-api-save-key" @click="saveKey">Save key</b-button>
                </div>
                <div class="od-api-row od-api-keystate">
                    <span data-test="od-api-key-set">
                        Key stored:
                        <strong :class="config.keySet ? 'text-success' : 'text-muted'">{{ config.keySet ? 'Yes' : 'No' }}</strong>
                    </span>
                    <b-button
                        v-if="config.keySet"
                        size="sm"
                        variant="link"
                        class="text-danger p-0 ml-3"
                        :disabled="saving || config.OD_API === 1"
                        :title="config.OD_API === 1 ? 'Turn the Open Dental API off before removing the key' : ''"
                        data-test="od-api-remove-key"
                        @click="removeKey"
                    >Remove key</b-button>
                </div>

                <!-- Exe secret (plan §1.15): how the office app proves which office it is. Shown ONCE. -->
                <div class="od-api-row od-api-keystate">
                    <span data-test="od-api-exe-secret-state">
                        Exe secret:
                        <strong :class="config.exeSecretIssuedAt ? 'text-success' : 'text-muted'">{{ config.exeSecretIssuedAt ? `issued ${config.exeSecretIssuedAt} UTC` : 'not issued' }}</strong>
                    </span>
                    <b-button
                        size="sm"
                        variant="link"
                        class="p-0 ml-3"
                        :disabled="saving || !!issuedSecret"
                        data-test="od-api-issue-secret"
                        @click="issueSecret"
                    >{{ config.exeSecretIssuedAt ? 'Reissue' : 'Issue exe secret' }}</b-button>
                </div>
                <div v-if="issuedSecret" class="od-api-secret-once" data-test="od-api-secret-once">
                    <div class="od-api-row mb-1">
                        <input
                            :value="issuedSecret"
                            type="text"
                            readonly
                            data-private
                            autocomplete="off"
                            spellcheck="false"
                            class="form-control form-control-sm od-api-key od-api-secret-text"
                            data-test="od-api-secret-value"
                            @focus="$event.target.select()"
                        />
                        <b-button size="sm" variant="outline-primary" data-test="od-api-copy-secret" @click="copySecret">Copy</b-button>
                        <b-button size="sm" variant="primary" data-test="od-api-secret-done" @click="clearIssuedSecret">Done</b-button>
                    </div>
                    <p class="od-api-note mb-0">{{ exeSecretHelp }}</p>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { getIdToken } from '@/pages/MyAccount/components/firebaseIdToken';
import {
    OD_API_TIER_DESCRIPTIONS,
    OD_API_REQUIREMENTS_NOTE,
    OD_API_EXE_SECRET_HELP,
    OD_API_REISSUE_CONFIRM,
    customerKeyProblem,
    turnOnBlockedReason,
    describeOdApiError,
} from '@/utils/odApiConfig';

export default {
    name: 'OdApiSettings',
    props: {
        siteId: { type: String, required: true },
    },
    data() {
        return {
            loading: false,
            loadError: null,
            saving: false,
            config: null,     // { OD_API, OpenDentalAPITier, keySet } — from the server only
            tierDraft: null,
            keyDraft: '',     // write-only: cleared after every save attempt
            toggleKey: 0,     // bumped to remount the switch at the server's value after a refusal/failure
            issuedSecret: '', // shown ONCE after Issue/Reissue; cleared on Done, on site change and on destroy
            exeSecretHelp: OD_API_EXE_SECRET_HELP,
            tiers: OD_API_TIER_DESCRIPTIONS,
            requirementsNote: OD_API_REQUIREMENTS_NOTE,
        };
    },
    computed: {
        blockedReason() {
            return turnOnBlockedReason(this.config);
        },
    },
    watch: {
        siteId() {
            this.clearIssuedSecret();
            this.load();
        },
    },
    mounted() {
        this.load();
    },
    beforeDestroy() {
        this.clearIssuedSecret();
    },
    methods: {
        async authHeaders() {
            const idToken = await getIdToken(this.$store.state.getAuth ? this.$store.state.getAuth() : null);
            return idToken ? { Authorization: `Bearer ${idToken}` } : {};
        },

        applyServerConfig(siteID, data) {
            if (siteID !== this.siteId) return; // a slow answer for a site we have since left
            this.config = {
                OD_API: data.OD_API === 1 ? 1 : 0,
                OpenDentalAPITier: data.OpenDentalAPITier || null,
                keySet: data.keySet === true,
                exeSecretIssuedAt: data.exeSecretIssuedAt || null,
            };
            this.tierDraft = this.config.OpenDentalAPITier;
        },

        async load() {
            const siteID = this.siteId;
            this.loading = true;
            this.loadError = null;
            this.config = null;
            try {
                const headers = await this.authHeaders();
                const res = await axios.get('/get-od-api-config', { params: { siteID }, crossdomain: true, headers });
                this.applyServerConfig(siteID, res.data || {});
            } catch (err) {
                if (siteID === this.siteId) this.loadError = describeOdApiError(err, 'Could not load the Open Dental API settings.');
            } finally {
                if (siteID === this.siteId) this.loading = false;
            }
        },

        async save(change, successMessage) {
            const siteID = this.siteId;
            this.saving = true;
            try {
                const headers = await this.authHeaders();
                const res = await axios.post('/save-od-api-config', { siteID, ...change }, { crossdomain: true, headers });
                this.applyServerConfig(siteID, res.data || {});
                this.$toast.success(successMessage);
                return true;
            } catch (err) {
                this.$toast.error(describeOdApiError(err, 'Could not save the Open Dental API settings.'));
                return false;
            } finally {
                this.saving = false;
            }
        },

        async onToggle(on) {
            if (on) {
                const reason = turnOnBlockedReason(this.config);
                if (reason) {
                    this.$toast.error(reason);
                    this.toggleKey += 1; // snap the switch back to the server's value
                    return;
                }
            }
            const ok = await this.save({ OD_API: on ? 1 : 0 }, on ? 'Open Dental API turned on' : 'Open Dental API turned off');
            if (!ok) this.toggleKey += 1;
        },

        async saveTier() {
            await this.save({ tier: this.tierDraft }, 'Open Dental API tier saved');
        },

        async saveKey() {
            const problem = customerKeyProblem(this.keyDraft);
            if (problem) {
                this.$toast.error(problem);
                return;
            }
            try {
                await this.save({ customerKey: this.keyDraft }, 'Customer key saved');
            } finally {
                this.keyDraft = ''; // never keep the key around, whatever the outcome
            }
        },

        async issueSecret() {
            if (this.config && this.config.exeSecretIssuedAt) {
                const confirmed = await this.$bvModal.msgBoxConfirm(OD_API_REISSUE_CONFIRM,
                    { title: 'Reissue exe secret', okVariant: 'danger', okTitle: 'Reissue', centered: true });
                if (!confirmed) return;
            }
            const siteID = this.siteId;
            this.saving = true;
            try {
                const headers = await this.authHeaders();
                const res = await axios.post('/issue-exe-secret', { siteID }, { crossdomain: true, headers });
                if (siteID !== this.siteId) return; // left the site meanwhile — never show it elsewhere
                const data = res.data || {};
                this.issuedSecret = data.exeSecret || '';
                this.config = { ...this.config, exeSecretIssuedAt: data.exeSecretIssuedAt || null };
                this.$toast.success('Exe secret issued — copy it now, it will not be shown again');
            } catch (err) {
                this.$toast.error(describeOdApiError(err, 'Could not issue the exe secret.'));
            } finally {
                this.saving = false;
            }
        },

        async copySecret() {
            try {
                await navigator.clipboard.writeText(this.issuedSecret);
                this.$toast.success('Copied');
            } catch (err) {
                this.$toast.error('Copy failed — select the text and copy it by hand');
            }
        },

        clearIssuedSecret() {
            this.issuedSecret = '';
        },

        async removeKey() {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                'Remove the stored Open Dental customer key for this office? You will need the key again to turn the Open Dental API back on.',
                { title: 'Remove customer key', okVariant: 'danger', okTitle: 'Remove', centered: true }
            );
            if (!confirmed) return;
            await this.save({ removeKey: true }, 'Customer key removed');
        },
    },
};
</script>

<style lang="scss" scoped>
/* Card shell copied from SuperAdmin.vue's scoped .feature-card rules (scoped CSS stops at a child's root). */
.feature-card {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    overflow: hidden;
    transition: box-shadow 0.2s;
}
.feature-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.feature-card-header {
    display: flex;
    align-items: center;
    padding: 12px 14px;
    gap: 12px;
}
.feature-icon {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 15px;
}
.feature-info {
    flex: 1;
    min-width: 0;
}
.feature-name {
    font-weight: 600;
    font-size: 13px;
    color: #2c3e50;
    line-height: 1.3;
}
.feature-desc {
    font-size: 11px;
    color: #95a5a6;
    line-height: 1.3;
}
.feature-sub-option {
    padding: 8px 14px 10px 62px;
    background: #f8f9fa;
    border-top: 1px solid #e9ecef;
}
.od-api-note {
    font-size: 11px;
    color: #6c757d;
    line-height: 1.4;
}
.od-api-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 6px;
}
.od-api-label {
    font-weight: 600;
    font-size: 12px;
    color: #2c3e50;
    margin: 0;
    min-width: 90px;
}
.od-api-select,
.od-api-key {
    flex: 1 1 220px;
    min-width: 0;
    max-width: 420px;
}
.od-api-legend {
    font-size: 11px;
    color: #6c757d;
    padding-left: 18px;
    line-height: 1.4;
}
.od-api-legend .od-api-current strong {
    color: #0f7259;
}
.od-api-items {
    padding-left: 16px;
    margin: 2px 0;
}
.od-api-requires {
    color: #b35c00;
}
.od-api-later {
    font-style: italic;
}
.od-api-keystate {
    font-size: 12px;
}
.od-api-secret-once {
    border: 1px dashed #0f7259;
    border-radius: 6px;
    padding: 8px;
    margin: 4px 0 6px;
    background: #fff;
}
.od-api-secret-text {
    font-family: monospace;
}
@media (max-width: 576px) {
    .feature-sub-option {
        padding-left: 14px;
    }
}
</style>
